import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import Image, { type StaticImageData } from 'next/image';
import type { CSSProperties, ReactNode } from 'react';
import usePrefersReducedMotion from '../usePrefersReducedMotion';
import styles from './PhotoMosaic.module.scss';
export interface PhotoMosaicItem {
  /**
   * A static import (preferred: the tile takes its ratio) or a path. The tile
   * crops it to fill.
   */
  src: StaticImageData | string;
  alt: string;
  /** The caption's first line, a small uppercase label: the person's name. */
  name: string;
  /**
   * A second label line under the name, in the same element: a year
   * ("Freshman"), or what a professor also teaches ("Also teaches Business
   * at:").
   */
  detail?: string;
  /** The caption's large line: a future role, a job or an institution. */
  description: string;
}

/**
 * The element of each caption's name line (with its `detail`). `p` by
 * default; a page that must keep its legacy caption headings passes their
 * level.
 */
export type PhotoMosaicNameElement = 'p' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/**
 * `frame` (the default): the mosaic in a framed slot beside or above a title
 * (the request-info modal). `backdrop`: the mosaic runs the full width of a
 * hero band behind its copy (`children`), on the page colour under the copy
 * (About, Tuition), or under a dark fade (`tone="inverse"`: Careers).
 */
export type PhotoMosaicLayout = 'frame' | 'backdrop';

/**
 * The backdrop's veil (Phase 6, the redesign's Careers hero). `default`: the
 * page colour, densest at the band's centre, the copy centred in it.
 * `inverse`: a dark fade (`surface-left-fade`, black at 60%) from the band's
 * foot below lg and from its left edge from lg, the copy at that edge in the
 * dark surface's text, in a band the page frames.
 */
export type PhotoMosaicTone = 'default' | 'inverse';
interface PhotoMosaicBaseProps {
  items: readonly PhotoMosaicItem[];
  /** The mosaic's accessible name (the group's label). */
  label: string;
  /**
   * One tile's rendered width, for `next/image` (`frame` draws two columns,
   * so a tile is about half the mosaic's width; `backdrop` four from md,
   * two below).
   */
  sizes: string;
  nameAs?: PhotoMosaicNameElement;
  /**
   * Loads the first tile of the first two columns early: the hero's LCP
   * candidates (the tiles at the top of the slot that every width shows).
   */
  priority?: boolean;
}
export interface PhotoMosaicFrameProps extends PhotoMosaicBaseProps {
  layout?: 'frame';
  /** The frame holds the photos only; its title sits outside it. */
  children?: never;
  /** The frame has no veil. */
  tone?: never;
}
export interface PhotoMosaicBackdropProps extends PhotoMosaicBaseProps {
  layout: 'backdrop';
  /**
   * The hero copy the mosaic runs behind (the title, an eyebrow or lead, the
   * calls to action), centred over it on the page colour. The page sets the
   * copy's own measure and type.
   */
  children: ReactNode;
  /**
   * The veil and where the copy sits: `default` (the page colour, the copy
   * centred) or `inverse` (a dark fade, the copy at its dark edge: the foot
   * below lg, the left from lg, within the fade's solid 60%).
   */
  tone?: PhotoMosaicTone;
}
export type PhotoMosaicProps = PhotoMosaicFrameProps | PhotoMosaicBackdropProps;

// The columns each layout splits its items into: `backdrop` draws four
// from md and parks the last two out of view below it (the stylesheet).
const COLUMNS: Record<PhotoMosaicLayout, number> = {
  frame: 2,
  backdrop: 4
};

// The columns whose first tile `priority` loads early: the two every width
// shows.
const PRIORITY_COLUMNS = 2;

// A tile takes its photo's own ratio (a static import carries its size), so
// a tall portrait stays tall and a wide one short, as the donor's per-person
// tile heights drew them; the stylesheet keeps every tile between a near
// square and twice its width (taller on phones, under a longer caption). A
// path has no size: its tile takes the stylesheet's 4:5.
function tileStyle(src: PhotoMosaicItem['src']) {
  if (typeof src === 'string' || !src.width || !src.height) return undefined;
  return {
    '--helios-photo-mosaic-ratio': Number((src.height / src.width).toFixed(4))
  } as CSSProperties;
}

// The items in order, split into the columns: each column takes a run of the
// list, the first ones one more when the count does not divide (two columns:
// the first takes the odd one), so the document reads the people in the
// order given, column by column.
function splitIntoColumns<T>(items: readonly T[], count: number) {
  const base = Math.floor(items.length / count);
  const extra = items.length % count;
  const columns: {
    start: number;
    items: readonly T[];
  }[] = [];
  let start = 0;
  for (let column = 0; column < count; column++) {
    const size = base + (column < extra ? 1 : 0);
    if (size > 0) columns.push({
      start,
      items: items.slice(start, start + size)
    });
    start += size;
  }
  return columns;
}

/**
 * The scrolling photo mosaic (Phase 5 follow-up, O229 B: the redesign
 * donor's GalleryScroller as a Helios component). Photo tiles with their
 * captions in columns, each column moving up continuously and looping (two
 * copies of its tiles, the second hidden from assistive technology and
 * inert; the column's loop time grows with its tile count, so every column
 * moves at about the same pace). Every caption sits at its tile's foot over
 * a dark scrim, white in both themes, at 4.5:1 or more on any photo.
 *
 * `layout="frame"` (the default) fills its slot in a rounded frame, two
 * columns: the slot sets the height, and the tiles are clipped at its edges;
 * hovering it holds it. `layout="backdrop"` runs the full width of its slot
 * behind `children`, the hero copy: four columns from md (two below, where
 * the other two are parked out of view), no frame, under the redesign
 * donor's veil, the page colour from 96% at the band's centre to clear at
 * its corners with a fade into the page at its foot, so the tiles show
 * through everywhere and most faded behind the centred copy. The backdrop
 * takes no pointer events and never holds. The copy reads at 4.5:1 over any
 * photo pixel while every point of it stays within 55.7% of the way from
 * the band's centre to its corner (the veil 55.4% opaque there): the page
 * keeps it there with its measure and the band's height, which the slot
 * sets (the mosaic grows past it for a taller copy).
 *
 * `tone="inverse"` (the backdrop only; Phase 6, the redesign's Careers
 * hero) lays a dark fade over the tiles instead: black at 60%
 * (`surface-left-fade`) over the band's lower 65% below lg and its left 60%
 * from lg, clearing towards the far edge. The copy sits at the dark edge (at
 * the foot below lg; vertically centred at the left from lg, at most 60% of
 * the band's content width) in the dark surface's text, with the dark
 * surface's tokens for what it holds and the focus ring drawn for media. It
 * reads at 5.74:1 or more over any photo pixel while it stays in the solid
 * part: the page keeps it there with the band's height (below lg). The page
 * frames the band (its corners, its width and its height).
 *
 * The mosaic is a group named by `label`. Under `prefers-reduced-motion`
 * the tiles do not move. The frame is then a static grid of every tile once
 * that scrolls inside its slot, and the group becomes a focusable region (as
 * DataTable's scroll region) that a keyboard can scroll, so every caption
 * can be read. The backdrop holds still and stays a group: a region that
 * scrolled under the copy would take the page's scroll.
 */
function PhotoMosaic(t0) {
  const $ = _c(34);
  const {
    items,
    label,
    sizes,
    nameAs: t1,
    priority: t2,
    layout: t3,
    children,
    tone: t4
  } = t0;
  const NameElement = t1 === undefined ? "p" : t1;
  const priority = t2 === undefined ? false : t2;
  const layout = t3 === undefined ? "frame" : t3;
  const tone = t4 === undefined ? "default" : t4;
  const reducedMotion = usePrefersReducedMotion();
  const backdrop = layout === "backdrop";
  const inverse = backdrop && tone === "inverse";
  if (items.length === 0 && !backdrop) {
    return null;
  }
  let t10;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== NameElement || $[1] !== backdrop || $[2] !== inverse || $[3] !== items || $[4] !== label || $[5] !== layout || $[6] !== priority || $[7] !== reducedMotion || $[8] !== sizes) {
    const columns = splitIntoColumns(items, COLUMNS[layout]);
    const scrollRegion = reducedMotion && !backdrop;
    let t11;
    if ($[15] !== NameElement || $[16] !== sizes) {
      t11 = (column, start, early) => column.map((item, index) => <li className={styles["helios-photo-mosaic__tile"]} key={`${start + index}-${item.name}`} style={tileStyle(item.src)}><figure className={styles["helios-photo-mosaic__figure"]}><Image alt={item.alt} className={styles["helios-photo-mosaic__image"]} fill={true} priority={early && index === 0} sizes={sizes} src={item.src} /><figcaption className={styles["helios-photo-mosaic__caption"]}><NameElement className={styles["helios-photo-mosaic__name"]}>{item.name}{item.detail && <><br />{item.detail}</>}</NameElement><p className={styles["helios-photo-mosaic__description"]}>{item.description}</p></figcaption></figure></li>);
      $[15] = NameElement;
      $[16] = sizes;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    const renderTiles = t11;
    let t12;
    if ($[18] !== sizes) {
      t12 = column_0 => column_0.map((item_0, index_0) => <li className={styles["helios-photo-mosaic__tile"]} key={`copy-${index_0}-${item_0.name}`} style={tileStyle(item_0.src)}><div className={styles["helios-photo-mosaic__figure"]}><Image alt="" className={styles["helios-photo-mosaic__image"]} fill={true} sizes={sizes} src={item_0.src} /><div className={styles["helios-photo-mosaic__caption"]}><p className={styles["helios-photo-mosaic__name"]}>{item_0.name}{item_0.detail && <><br />{item_0.detail}</>}</p><p className={styles["helios-photo-mosaic__description"]}>{item_0.description}</p></div></div></li>);
      $[18] = sizes;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    const renderCopies = t12;
    const t13 = styles[`helios-photo-mosaic--${layout}`];
    const t14 = inverse && styles["helios-photo-mosaic--tone-inverse"];
    if ($[20] !== t13 || $[21] !== t14) {
      t5 = classNames(styles["helios-photo-mosaic"], t13, t14);
      $[20] = t13;
      $[21] = t14;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    t6 = "";
    t7 = layout;
    t8 = reducedMotion ? "reduce" : undefined;
    t9 = inverse ? "inverse" : undefined;
    t10 = columns.length > 0 && <div className={styles["helios-photo-mosaic__viewport"]} role={scrollRegion ? "region" : "group"} aria-label={label} tabIndex={scrollRegion ? 0 : undefined}>{columns.map((column_1, columnIndex) => <div className={styles["helios-photo-mosaic__column"]} key={column_1.start}><div className={styles["helios-photo-mosaic__track"]} data-helios-photo-mosaic-track="" style={{
          "--helios-photo-mosaic-tiles": column_1.items.length
        } as CSSProperties}><ul className={styles["helios-photo-mosaic__list"]}>{renderTiles(column_1.items, column_1.start, priority && columnIndex < PRIORITY_COLUMNS)}</ul><ul className={classNames(styles["helios-photo-mosaic__list"], styles["helios-photo-mosaic__list--copy"])} aria-hidden="true" inert={true} data-helios-photo-mosaic-copy="">{renderCopies(column_1.items)}</ul></div></div>)}</div>;
    $[0] = NameElement;
    $[1] = backdrop;
    $[2] = inverse;
    $[3] = items;
    $[4] = label;
    $[5] = layout;
    $[6] = priority;
    $[7] = reducedMotion;
    $[8] = sizes;
    $[9] = t10;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t10 = $[9];
    t5 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  let t11;
  if ($[23] !== backdrop || $[24] !== children) {
    t11 = backdrop && <div className={styles["helios-photo-mosaic__content"]} data-helios-photo-mosaic-content="">{children}</div>;
    $[23] = backdrop;
    $[24] = children;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t11 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9) {
    t12 = <div className={t5} data-helios-photo-mosaic={t6} data-layout={t7} data-motion={t8} data-tone={t9}>{t10}{t11}</div>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t9;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
}
export default PhotoMosaic;